import React from 'react';
import OptionalPhotoUpload from '../common/OptionalPhotoUpload';

/**
 * SaleModal — nayi sale record karna (Cash ya EMI).
 * Customer search, IMEI selection, reward points, aur witness sab yahan.
 * Sabhi state Inventory.js mein hai; ye component sirf UI hai.
 *
 * Props (state):
 *   show, saleData, products, selectedProductId,
 *   selectedCustomer, customerSearchResults, customerSearchLoading,
 *   showNewCustomerForm, newCustomer,
 *   isSelfWitness, selectedWitness,
 *   witnessSearchTerm, witnessSearchResults, isWitnessSearching,
 *   error, submittingSale
 *
 * Props (callbacks):
 *   onSaleDataChange(updated)    — setSaleData
 *   onSearchCustomer()           — searchCustomerByPhone
 *   onSelectCustomer(customer)   — handleSelectCustomer
 *   onToggleNewCustomerForm()    — toggle showNewCustomerForm
 *   onNewCustomerChange(e)       — handleNewCustomerChange
 *   onSetIsSelfWitness(bool)
 *   onSetSelectedWitness(obj)
 *   onWitnessSearchChange(val)   — setWitnessSearchTerm + searchWitnesses
 *   onClearWitnessSearch()
 *   onSubmit()                   — submitSaleRecord
 *   onClose()                    — setShowSaleModal(false)
 */
export default function SaleModal({
  show,
  saleData,
  products,
  selectedProductId,
  selectedCustomer,
  customerSearchResults,
  customerSearchLoading,
  showNewCustomerForm,
  newCustomer,
  isSelfWitness,
  selectedWitness,
  witnessSearchTerm,
  witnessSearchResults,
  isWitnessSearching,
  error,
  submittingSale,
  salePhotoFile,
  onSalePhotoChange,
  onSaleDataChange,
  onSearchCustomer,
  onSelectCustomer,
  onToggleNewCustomerForm,
  onNewCustomerChange,
  onSetIsSelfWitness,
  onSetSelectedWitness,
  onWitnessSearchChange,
  onClearWitnessSearch,
  onSubmit,
  onClose
}) {
  if (!show) return null;

  const set = (field, value) => onSaleDataChange({ ...saleData, [field]: value });
  const setEmi = (field, value) =>
    onSaleDataChange({ ...saleData, emiDetails: { ...saleData.emiDetails, [field]: value } });

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const calcEmiDetails = () => {
    const price = parseFloat(saleData.salePrice) || 0;
    const rewardDiscount =
      saleData.useRewardPoints && saleData.rewardPointsToUse > 0
        ? Math.min(saleData.rewardPointsToUse, price)
        : 0;
    const effectivePrice = Math.max(0, price - rewardDiscount);
    const dp = parseFloat(saleData.emiDetails.downPayment) || 0;
    if (effectivePrice <= 0 || dp <= 0 || dp >= effectivePrice) {
      return { principal: 0, interest: 0, totalAmount: 0, monthlyAmount: 0, totalWithDownPayment: 0, effectivePrice, rewardDiscount };
    }
    const principal = effectivePrice - dp;
    const rate = saleData.emiDetails.interestRate ? parseFloat(saleData.emiDetails.interestRate) : 0;
    const interest = (principal * rate * saleData.emiDetails.months) / 100;
    const totalAmount = principal + interest;
    const monthlyAmount = totalAmount / saleData.emiDetails.months;
    return { principal, interest, totalAmount, monthlyAmount, totalWithDownPayment: totalAmount + dp, effectivePrice, rewardDiscount };
  };

  const product = selectedProductId ? products.find((p) => p._id === selectedProductId) : null;
  const isMobile = product && (product.category === 'Mobile' || product.category === 'Old Mobile');
  const hasImeiUnits = isMobile && product?.imeiTracking?.length > 0;
  const availableImeis = product?.imeiTracking
    ? product.imeiTracking.filter((imei) => imei.status === 'available')
    : [];
  const emiCalc = calcEmiDetails();
  const salePriceNum = parseFloat(saleData.salePrice) || 0;
  const maxRewardPoints = selectedCustomer
    ? Math.min(selectedCustomer.rewardPoints || 0, Math.floor(salePriceNum))
    : 0;

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header bg-success text-white">
            <h5 className="modal-title">Record Sale</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}

            {/* Date + Quantity */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Sale Date</label>
                <input
                  type="date" className="form-control"
                  value={saleData.date}
                  onChange={(e) => set('date', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Quantity</label>
                <input
                  type="number" className="form-control" min="1"
                  value={saleData.quantity}
                  onChange={(e) => set('quantity', e.target.value)}
                  disabled={saleData.isEmi}
                />
                {!hasImeiUnits && !saleData.isEmi && (
                  <div className="form-text">
                    Har piece alag history row banegi — customer naam/phone save karein taaki baad mein pata chale
                    kisne liya.
                  </div>
                )}
                {saleData.isEmi && (
                  <div className="form-text">EMI hamesha 1 unit hoti hai.</div>
                )}
              </div>
            </div>

            {/* Sale Price */}
            <div className="mb-3">
              <label className="form-label">Sale Price</label>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input
                  type="number" className="form-control" step="0.01" required
                  value={saleData.salePrice}
                  onChange={(e) => {
                    const newPrice = e.target.value;
                    const dp = newPrice ? Math.round(parseFloat(newPrice) * 0.4) : '';
                    onSaleDataChange({
                      ...saleData,
                      salePrice: newPrice,
                      emiDetails: { ...saleData.emiDetails, downPayment: dp.toString() }
                    });
                  }}
                />
              </div>
            </div>

            {/* Sale Type */}
            <div className="mb-3">
              <label className="form-label fw-bold">Sale Type</label>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-check border rounded p-3 h-100">
                    <input
                      className="form-check-input" type="radio" name="saleType" id="cashSale"
                      checked={!saleData.isEmi}
                      onChange={() => set('isEmi', false)}
                    />
                    <label className="form-check-label fw-bold text-success" htmlFor="cashSale">
                      <i className="bi bi-cash-coin me-2"></i>Cash Sale
                    </label>
                    <div className="text-muted small mt-1">Direct payment, no witness needed</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check border rounded p-3 h-100">
                    <input
                      className="form-check-input" type="radio" name="saleType" id="emiSale"
                      checked={saleData.isEmi}
                      onChange={() => set('isEmi', true)}
                    />
                    <label className="form-check-label fw-bold text-primary" htmlFor="emiSale">
                      <i className="bi bi-credit-card me-2"></i>Sell with EMI
                    </label>
                    <div className="text-muted small mt-1">Installment payment with witness</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3 border-secondary">
              <div className="card-header bg-light py-2">
                <h6 className="mb-0">
                  <i className="bi bi-camera me-2"></i>
                  Sale Photo
                </h6>
              </div>
              <div className="card-body py-3">
                <OptionalPhotoUpload
                  title=""
                  file={salePhotoFile}
                  onChange={onSalePhotoChange}
                  hint="Customer / product ki photo — baad me customer profile → Purchase History me dikhegi."
                  className="mb-0"
                />
              </div>
            </div>

            {/* EMI Details */}
            {saleData.isEmi && (
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <h6 className="mb-0">EMI Details</h6>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">EMI Tenure (Months)</label>
                      <select
                        className="form-select"
                        value={saleData.emiDetails.months}
                        onChange={(e) => {
                          const months = parseInt(e.target.value);
                          const dp = Math.round((parseFloat(saleData.salePrice) || 0) * 0.4);
                          onSaleDataChange({
                            ...saleData,
                            emiDetails: { ...saleData.emiDetails, months, downPayment: dp.toString() }
                          });
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6].map((m) => (
                          <option key={m} value={m}>{m} Month{m > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Interest Rate (%) - Optional</label>
                      <input
                        type="number" className="form-control" step="0.1" min="0"
                        placeholder="0% by default"
                        value={saleData.emiDetails.interestRate}
                        onChange={(e) => setEmi('interestRate', parseFloat(e.target.value))}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Down Payment</label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number" className="form-control"
                          value={saleData.emiDetails.downPayment}
                          onChange={(e) => setEmi('downPayment', e.target.value)}
                        />
                      </div>
                      {saleData.salePrice && saleData.emiDetails.downPayment && parseFloat(saleData.salePrice) > 0 && (
                        <small className="text-muted">
                          {Math.round((parseFloat(saleData.emiDetails.downPayment) / parseFloat(saleData.salePrice)) * 100)}% of selling price
                        </small>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Preferred Payment Date</label>
                      <select
                        className="form-select"
                        value={saleData.emiDetails.preferredPaymentDate}
                        onChange={(e) => setEmi('preferredPaymentDate', parseInt(e.target.value))}
                      >
                        {[...Array(31)].map((_, i) => (
                          <option key={i} value={i + 1}>
                            {i + 1}{getOrdinalSuffix(i + 1)} of each month
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {saleData.salePrice && saleData.emiDetails.downPayment && (
                    <div className="alert alert-info">
                      <h6 className="alert-heading mb-3">EMI Summary</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-2"><strong>Sale Price:</strong> ₹{parseFloat(saleData.salePrice).toLocaleString()}</div>
                          <div className="mb-2"><strong>Down Payment:</strong> ₹{parseFloat(saleData.emiDetails.downPayment).toLocaleString()}</div>
                          <div className="mb-2"><strong>Principal Amount:</strong> ₹{emiCalc.principal.toLocaleString()}</div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-2"><strong>Interest:</strong> ₹{emiCalc.interest.toLocaleString()}</div>
                          <div className="mb-2"><strong>Total Cost:</strong> ₹{emiCalc.totalWithDownPayment.toLocaleString()}</div>
                          <div className="mb-0">
                            <h5 className="text-success mb-0">Monthly Payment: ₹{emiCalc.monthlyAmount.toFixed(0)}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* IMEI Selection */}
            {isMobile && (
              <div className="card mb-3">
                <div className="card-header bg-info text-white">
                  <h6 className="mb-0">IMEI Selection</h6>
                </div>
                <div className="card-body">
                  {availableImeis.length > 0 ? (
                    <div>
                      <label className="form-label">Select IMEI to Sell</label>
                      <div className="row">
                        {availableImeis.map((imei) => {
                          const imeiIndex = product.imeiTracking.indexOf(imei);
                          return (
                            <div key={imeiIndex} className="col-md-6 mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input" type="radio"
                                  name="imeiSelection" id={`imei-${imeiIndex}`}
                                  checked={saleData.selectedImeiIndex === imeiIndex}
                                  onChange={() => set('selectedImeiIndex', imeiIndex)}
                                />
                                <label className="form-check-label" htmlFor={`imei-${imeiIndex}`}>
                                  <strong>IMEI 1:</strong> {imei.imei1}<br />
                                  {imei.imei2 && <><strong>IMEI 2:</strong> {imei.imei2}</>}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <small className="text-muted">
                        Available IMEIs: {availableImeis.length} | Total Stock: {product.stock}
                      </small>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      No IMEIs available for this mobile device. Please add IMEIs first using "Add Stock" button.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Customer Information */}
            <div className="card mb-3">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  Customer Information
                  {saleData.isEmi && <span className="text-danger">*</span>}
                  {!saleData.isEmi && <span className="text-muted"> (Optional for Cash Sale)</span>}
                </h6>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Customer Phone</label>
                    <div className="input-group">
                      <input
                        type="text" className="form-control" maxLength="10" pattern="[0-9]{10}"
                        value={saleData.customerPhone}
                        placeholder={saleData.isEmi ? 'Enter 10-digit mobile number' : 'Optional for cash sale'}
                        required={saleData.isEmi}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          onSaleDataChange({ ...saleData, customerPhone: value });
                        }}
                        onKeyPress={(e) => { if (!/\d/.test(e.key)) e.preventDefault(); }}
                      />
                      <button
                        className="btn btn-outline-secondary" type="button"
                        onClick={onSearchCustomer}
                        disabled={!saleData.customerPhone || customerSearchLoading}
                      >
                        {customerSearchLoading
                          ? <span className="spinner-border spinner-border-sm" />
                          : <i className="bi bi-search"></i>}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Customer Name</label>
                    <input
                      type="text" className="form-control"
                      value={saleData.customerName}
                      placeholder={saleData.isEmi ? 'Required for EMI' : 'Optional for cash sale'}
                      required={saleData.isEmi}
                      onChange={(e) => set('customerName', e.target.value)}
                    />
                  </div>
                </div>

                {customerSearchResults.length > 0 && (
                  <div className="mb-3">
                    <div className="alert alert-info">
                      <h6>Select Existing Customer:</h6>
                      <div className="list-group">
                        {customerSearchResults.map((c) => (
                          <button
                            key={c._id} type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => onSelectCustomer(c)}
                          >
                            <strong>{c.name}</strong> - {c.phone}
                            {c.isRiskyCustomer && (
                              <span className="badge bg-danger ms-2">⚠️ Risky</span>
                            )}
                            {c.totalPurchases > 0 && (
                              <span className="badge bg-success ms-2">{c.totalPurchases} previous purchases</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {selectedCustomer && (
                  <div className={`alert mb-3 ${selectedCustomer.isRiskyCustomer ? 'alert-danger' : 'alert-success'}`}>
                    <h6>Selected Customer:</h6>
                    <p className="mb-1">
                      <strong>{selectedCustomer.name}</strong> - {selectedCustomer.phone}
                      {selectedCustomer.isRiskyCustomer && (
                        <span className="badge bg-danger ms-2">⚠️ Risky Customer</span>
                      )}
                    </p>
                    {selectedCustomer.isRiskyCustomer && saleData.isEmi && (
                      <p className="mb-2 small fw-bold">
                        Pehle NPA settle hua tha — nayi EMI dene se pehle management se confirm karein.
                      </p>
                    )}
                    {(selectedCustomer.totalPurchases > 0 || selectedCustomer.rewardPoints > 0) && (
                      <p className="mb-0">
                        {selectedCustomer.totalPurchases > 0 && (
                          <>
                            <span className="badge bg-info me-2">{selectedCustomer.totalPurchases} purchases</span>
                            <span className="badge bg-warning">₹{selectedCustomer.totalSpent?.toLocaleString()} spent</span>
                          </>
                        )}
                        {selectedCustomer.rewardPoints > 0 && (
                          <span className="badge bg-success ms-2">{selectedCustomer.rewardPoints} reward points</span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {selectedCustomer && selectedCustomer.rewardPoints > 0 && (
                  <div className="card mb-3">
                    <div className="card-header bg-warning text-dark">
                      <h6 className="mb-0"><i className="bi bi-gift me-2"></i>Use Reward Points</h6>
                    </div>
                    <div className="card-body">
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input" type="checkbox" id="useRewardPoints"
                          checked={saleData.useRewardPoints}
                          onChange={(e) =>
                            onSaleDataChange({
                              ...saleData,
                              useRewardPoints: e.target.checked,
                              rewardPointsToUse: e.target.checked ? maxRewardPoints : 0
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="useRewardPoints">
                          Use reward points for this purchase
                        </label>
                      </div>
                      {saleData.useRewardPoints && (
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label">Points to Use</label>
                            <input
                              type="number" className="form-control" min="0"
                              max={maxRewardPoints}
                              value={saleData.rewardPointsToUse}
                              onChange={(e) =>
                                set('rewardPointsToUse', Math.min(parseInt(e.target.value, 10) || 0, maxRewardPoints))
                              }
                            />
                            <div className="form-text">
                              Available: {selectedCustomer.rewardPoints} points · Max usable: {maxRewardPoints} (sale price tak)
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Discount Amount</label>
                            <div className="input-group">
                              <span className="input-group-text">₹</span>
                              <input type="text" className="form-control" value={saleData.rewardPointsToUse} readOnly />
                            </div>
                            <div className="form-text">
                              Final amount: ₹{Math.max(0, salePriceNum - (saleData.rewardPointsToUse || 0)).toLocaleString()}
                              {saleData.isEmi && saleData.rewardPointsToUse > 0 && (
                                <span className="d-block text-muted">EMI bhi is discounted amount par calculate hogi</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedCustomer || saleData.customerPhone) && saleData.isEmi && (
                  <div className="mb-3">
                    <button
                      type="button" className="btn btn-sm btn-outline-primary"
                      onClick={onToggleNewCustomerForm}
                    >
                      {showNewCustomerForm ? 'Hide' : 'Add'} Additional Customer Details
                    </button>
                    {showNewCustomerForm && (
                      <div className="card mt-2">
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Email (Optional)</label>
                              <input
                                type="email" className="form-control"
                                value={newCustomer.email} name="email"
                                onChange={onNewCustomerChange}
                              />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label className="form-label">ID Proof (Optional)</label>
                              <input
                                type="text" className="form-control"
                                value={newCustomer.idProof} name="idProof"
                                placeholder="Aadhar/PAN/Driving License"
                                onChange={onNewCustomerChange}
                              />
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Address (Optional)</label>
                            <textarea
                              className="form-control" rows="2"
                              value={newCustomer.address} name="address"
                              onChange={onNewCustomerChange}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Witness — only for EMI */}
            {saleData.isEmi && (
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <h6 className="mb-0">Witness Information</h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input" type="checkbox" id="selfWitness"
                          checked={isSelfWitness}
                          onChange={(e) => {
                            onSetIsSelfWitness(e.target.checked);
                            if (e.target.checked) onSetSelectedWitness(null);
                          }}
                        />
                        <label className="form-check-label" htmlFor="selfWitness">
                          Self Witness (Customer witnesses for themselves)
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      {!isSelfWitness && (
                        <div className="position-relative">
                          <input
                            type="text" className="form-control"
                            placeholder="Search witness by name or phone..."
                            value={witnessSearchTerm}
                            onChange={(e) => onWitnessSearchChange(e.target.value)}
                            onFocus={() => { if (witnessSearchTerm.length >= 2) onWitnessSearchChange(witnessSearchTerm); }}
                          />
                          {witnessSearchResults.length > 0 && (
                            <div
                              className="position-absolute w-100 bg-white border rounded shadow-lg"
                              style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                            >
                              {witnessSearchResults
                                .filter((c) => c._id !== selectedCustomer?._id)
                                .map((c) => (
                                  <div
                                    key={c._id}
                                    className="p-2 border-bottom"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      onSetSelectedWitness(c);
                                      onWitnessSearchChange(`${c.name} - ${c.phone}`);
                                      onClearWitnessSearch();
                                    }}
                                  >
                                    <div className="fw-bold">{c.name}</div>
                                    <small className="text-muted">{c.phone}</small>
                                    {c.witnessProfile?.totalReferrals > 0 && (
                                      <small className="text-success ms-2">
                                        ({c.witnessProfile.totalReferrals} referrals)
                                      </small>
                                    )}
                                  </div>
                                ))}
                            </div>
                          )}
                          {isWitnessSearching && (
                            <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Searching...</span>
                              </div>
                            </div>
                          )}
                          {witnessSearchTerm && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary position-absolute top-50 end-0 translate-middle-y me-3"
                              onClick={onClearWitnessSearch}
                              style={{ zIndex: 1001 }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {(selectedWitness || isSelfWitness) && (
                    <div className="alert alert-info mb-0 mt-2">
                      Witness:{' '}
                      <strong>{isSelfWitness ? selectedCustomer?.name : selectedWitness?.name}</strong>{' '}
                      ({isSelfWitness ? selectedCustomer?.phone : selectedWitness?.phone})
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button" className="btn btn-success"
              onClick={onSubmit} disabled={submittingSale}
            >
              {submittingSale ? 'Processing...' : 'Record Sale'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
